import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell, Customized } from 'recharts';

// Calculate dispersion ellipse (1σ and 2σ)
const calculateEllipse = (shots) => {
  if (shots.length < 2) return null;

  const meanX = shots.reduce((sum, s) => sum + s.x, 0) / shots.length;
  const meanY = shots.reduce((sum, s) => sum + s.y, 0) / shots.length;

  const varianceX = shots.reduce((sum, s) => sum + Math.pow(s.x - meanX, 2), 0) / shots.length;
  const varianceY = shots.reduce((sum, s) => sum + Math.pow(s.y - meanY, 2), 0) / shots.length;

  const radiusX = Math.sqrt(varianceX);
  const radiusY = Math.sqrt(varianceY);

  // Validate all values before returning
  if (isNaN(meanX) || isNaN(meanY) || isNaN(radiusX) || isNaN(radiusY) ||
      radiusX <= 0 || radiusY <= 0) {
    return null;
  }

  return {
    centerX: meanX,
    centerY: meanY,
    radiusX,
    radiusY
  };
};

// Render dispersion ellipses
const renderEllipses = (props) => {
  const { xAxis, yAxis, ellipses } = props;
  if (!ellipses || ellipses.length === 0 || !xAxis || !yAxis) return null;

  return (
    <g>
      {ellipses.map((ellipse, idx) => {
        // Convert data coordinates to pixel coordinates
        const cx = xAxis.scale(ellipse.centerX);
        const cy = yAxis.scale(ellipse.centerY);
        const rx1 = Math.abs(xAxis.scale(ellipse.centerX + ellipse.radiusX) - cx); // 1σ
        const ry1 = Math.abs(yAxis.scale(ellipse.centerY + ellipse.radiusY) - cy);
        const rx2 = rx1 * 2; // 2σ
        const ry2 = ry1 * 2;

        // Skip rendering if any values are invalid
        if (isNaN(cx) || isNaN(cy) || isNaN(rx1) || isNaN(ry1) ||
            isNaN(rx2) || isNaN(ry2) || rx1 <= 0 || ry1 <= 0) {
          return null;
        }

        return (
          <g key={`ellipse-group-${idx}`}>
            {/* 2σ ellipse (95% confidence) */}
            <ellipse
              cx={cx}
              cy={cy}
              rx={rx2}
              ry={ry2}
              fill="none"
              stroke={ellipse.color}
              strokeWidth={1}
              strokeOpacity={0.2}
              strokeDasharray="5 3"
            />
            {/* 1σ ellipse (68% confidence) */}
            <ellipse
              cx={cx}
              cy={cy}
              rx={rx1}
              ry={ry1}
              fill="none"
              stroke={ellipse.color}
              strokeWidth={1.5}
              strokeOpacity={0.4}
            />
          </g>
        );
      })}
    </g>
  );
};

export const DispersionChart = React.memo(function DispersionChart({
  shotGroups,
  selectedShots = [],
  showAverage = true,
  distanceType = 'carry'
}) {
  const { scatterData, ellipses } = useMemo(() => {
    const allShots = [];
    const ellipseData = [];

    shotGroups.forEach(group => {
      const shots = group.shots.filter(s => s.included);
      const groupShots = [];

      shots.forEach(shot => {
        const distance = distanceType === 'carry' ? shot.metrics.carry : shot.metrics.total;

        // Calculate offline distance - handle multiple field names and missing data
        let offline = shot.metrics.offlineDistance || shot.metrics.offline || 0;

        // If offline not available, estimate from spin axis (rough approximation)
        if (offline === 0 && shot.metrics.spinAxis && distance) {
          // Positive spin axis = right, negative = left
          // Rough approximation: 1 degree spin axis ≈ 1.5-2 yards offline at typical driver distance
          const calculated = (shot.metrics.spinAxis * distance) / 150;
          offline = isNaN(calculated) ? 0 : calculated;
        }

        // Validate values are numbers and not NaN
        if (distance !== null && distance !== undefined && !isNaN(distance) &&
            offline !== null && offline !== undefined && !isNaN(offline)) {
          const point = {
            x: distance,  // Carry/Total on X-axis
            y: offline,   // Offline on Y-axis
            groupId: group.id,
            groupName: group.name,
            color: group.color,
            shotNumber: shot.shotNumber,
            isSelected: selectedShots.some(
              s => s.groupId === group.id && s.shotNumber === shot.shotNumber
            )
          };
          allShots.push(point);
          groupShots.push({ x: distance, y: offline });
        }
      });

      // Calculate ellipse for this group
      if (groupShots.length >= 2) {
        const ellipse = calculateEllipse(groupShots);
        if (ellipse) {
          ellipseData.push({
            ...ellipse,
            groupId: group.id,
            color: group.color
          });
        }
      }
    });

    return { scatterData: allShots, ellipses: ellipseData };
  }, [shotGroups, selectedShots, showAverage, distanceType]);

  if (scatterData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-text-muted">
        No dispersion data available
      </div>
    );
  }

  return (
    <div className="w-full h-96 bg-background rounded-lg">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 30, bottom: 50, left: 60 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#333333"
            strokeOpacity={0.3}
          />

          <XAxis
            type="number"
            dataKey="x"
            name={distanceType === 'carry' ? 'Carry' : 'Total'}
            unit=" yds"
            stroke="#9ca3af"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            label={{
              value: `${distanceType === 'carry' ? 'Carry' : 'Total'} Distance (yards)`,
              position: 'insideBottom',
              offset: -15,
              fill: '#9ca3af',
              fontSize: 13
            }}
          />

          <YAxis
            type="number"
            dataKey="y"
            name="Offline"
            unit=" yds"
            stroke="#9ca3af"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            label={{
              value: 'Offline (yards)',
              angle: -90,
              position: 'insideLeft',
              fill: '#9ca3af',
              fontSize: 13
            }}
          />

          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{
              backgroundColor: '#18181b',
              border: '1px solid #52525b',
              borderRadius: '8px',
              padding: '10px 14px',
              color: '#fafafa',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            }}
            labelStyle={{ color: '#a1a1aa', fontWeight: 600, marginBottom: 4 }}
            itemStyle={{ color: '#fafafa' }}
            formatter={(value, name) => [
              `${value > 0 && name === 'y' ? '+' : ''}${Math.round(value)} yds`,
              name === 'x' ? (distanceType === 'carry' ? 'Carry' : 'Total') : 'Offline'
            ]}
          />

          {/* Center line at Y=0 — solid line for clear left/right reference */}
          <ReferenceLine y={0} stroke="#a1a1aa" strokeWidth={2} label={{ value: '0 yds', fill: '#a1a1aa', fontSize: 12, position: 'right' }} />

          {/* Render dispersion ellipses */}
          <Customized component={(props) => renderEllipses({ ...props, ellipses })} />

          <Scatter data={scatterData}>
            {scatterData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isSelected ? '#3b82f6' : entry.color}
                fillOpacity={entry.isSelected ? 1 : 0.6}
                r={entry.isSelected ? 6 : 4}
                stroke={entry.isSelected ? '#60a5fa' : 'none'}
                strokeWidth={entry.isSelected ? 2 : 0}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
});

DispersionChart.propTypes = {
  shotGroups: PropTypes.array.isRequired,
  selectedShots: PropTypes.array,
  showAverage: PropTypes.bool,
  distanceType: PropTypes.oneOf(['carry', 'total'])
};

export default DispersionChart;
